/**
 * รับของเข้าสต๊อก — `POST /v1/goods-receipts`
 *
 * ฟอร์มนี้มีหกช่อง และ **ไม่มีช่องที่เจ็ด** ช่องที่หายไปอย่างตั้งใจที่สุดคือ
 * **สถานะคุณภาพ**: D-059 ตัดมันออกจาก payload ทั้งหมด เพราะ field ที่ client
 * เลือกได้คือช่องข้ามการกักกัน และการกักที่ข้ามได้ด้วยการส่งค่าหนึ่งใน request
 * ไม่ใช่การกัก ของทุกกองที่รับเข้ามาลงที่ `QUALITY_HOLD` เสมอ และหน้าจอนี้พูดถึง
 * มันสองครั้ง — ก่อนกดบันทึก และหลังบันทึกสำเร็จ
 *
 * **คีย์กันซ้ำคือสาระของหน้าจอนี้ ไม่ใช่ header ที่เติมให้ครบ** คีย์ถูกผูกไว้กับ
 * เนื้อของคำขอ (ดู `lib/goodsReceipts/idempotency.ts`): กดซ้ำหลังเน็ตหลุดจะส่ง
 * คีย์เดิม เซิร์ฟเวอร์จึงคืนใบเดิมแทนที่จะรับเหล็กเข้าเป็นกองที่สอง ส่วนใบใหม่
 * ได้คีย์ใหม่เสมอ
 *
 * ⚠️ `quantity` เดินทางเป็น **string** ตลอด ไม่ผ่าน `Number` แม้แต่ตอนตรวจค่า —
 * `NUMERIC(18,4)` ที่ผ่าน IEEE-754 double คือจำนวนที่เพี้ยนไปเงียบ ๆ และ
 * 250.0000 สำคัญเมื่อมันคือเหล็ก
 *
 * ⚠️ วันที่ที่มีผลประกอบจาก `lib/businessTime.ts` **ที่เดียว** (D-055) — offset
 * เป็นของโรงงานเสมอ ไม่ใช่ของเครื่องที่ผู้ใช้นั่งอยู่ งวดบัญชีที่ `posting_date`
 * ตกลงไปต้องเป็นงวดเดียวกันไม่ว่าใครเป็นคนกรอก
 */

import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { GoodsReceiptFailureNotice } from './GoodsReceiptFailureNotice';
import { qualityStatusLabel, qualityStatusTone, receiptDetailSearch } from './goodsReceiptView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { isPositiveDecimal } from '../../lib/bom/types';
import { businessDayToIso } from '../../lib/businessTime';
import {
  createGoodsReceipt,
  serializeCreateGoodsReceiptBody,
  type GoodsReceiptFailure,
} from '../../lib/goodsReceipts/client';
import { submissionFor, type ReceiptSubmission } from '../../lib/goodsReceipts/idempotency';
import {
  QUALITY_HOLD,
  type CreateGoodsReceiptBody,
  type GoodsReceipt,
} from '../../lib/goodsReceipts/types';

export interface GoodsReceiptCreateViewProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** โรงงานที่ `plant_scope` ของผู้ใช้พามาถึงจริง (D-004) */
  readonly plants: readonly string[];
  readonly listHref: string;
}

type FormState =
  | { readonly status: 'idle' }
  | { readonly status: 'invalid'; readonly problems: readonly string[] }
  | { readonly status: 'saving' }
  | { readonly status: 'failed'; readonly failure: GoodsReceiptFailure }
  | { readonly status: 'posted'; readonly receipt: GoodsReceipt };

export function GoodsReceiptCreateView({
  apiBaseUrl,
  fetchImpl,
  plants,
  listHref,
}: GoodsReceiptCreateViewProps) {
  const { pathname } = useLocation();
  const [plantCode, setPlantCode] = useState(plants[0] ?? '');
  const [productCode, setProductCode] = useState('');
  const [storageLocationCode, setStorageLocationCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [postingDay, setPostingDay] = useState('');
  const [supplierLotId, setSupplierLotId] = useState('');
  const [state, setState] = useState<FormState>({ status: 'idle' });

  /**
   * คำขอครั้งล่าสุดที่ถูกส่งออกไป พร้อมคีย์ที่มันถือ
   *
   * `null` แปลว่า "ยังไม่มีใบที่ค้างอยู่" ซึ่งเกิดได้สองทาง: ยังไม่เคยกดบันทึก
   * หรือใบก่อนหน้าสำเร็จไปแล้ว — ทั้งสองกรณีคำขอถัดไปคือ **ใบใหม่** และต้องได้
   * คีย์ใหม่ ส่วนการกดซ้ำด้วยข้อมูลเดิมหลังจากล้มเหลวคือใบเดิม และต้องได้คีย์เดิม
   */
  const [submission, setSubmission] = useState<ReceiptSubmission | null>(null);

  const resetForNextReceipt = () => {
    setProductCode('');
    setStorageLocationCode('');
    setQuantity('');
    setPostingDay('');
    setSupplierLotId('');
    setSubmission(null);
    setState({ status: 'idle' });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (state.status === 'saving') {
      return;
    }

    /**
     * ตรวจที่หน้าจอเพื่อไม่ให้ผู้ใช้รอคำตอบที่รู้ล่วงหน้าแล้วว่าเป็นการปฏิเสธ —
     * **ไม่ใช่การแทนที่ด่านของเซิร์ฟเวอร์** ซึ่งตรวจซ้ำทุกข้อทุกคำขอเสมอ
     */
    const problems: string[] = [];
    const plant = plantCode.trim();
    const product = productCode.trim();
    const location = storageLocationCode.trim();
    // ⚠️ ไม่ trim ค่า NUMERIC: สิ่งที่พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร
    const postingDate = businessDayToIso(postingDay);
    const supplierLot = supplierLotId.trim();

    if (plant === '') {
      problems.push('ต้องเลือกโรงงาน — การรับเข้าเป็นของโรงงานใดโรงงานหนึ่งเสมอ (D-004)');
    }
    if (product === '') {
      problems.push('ต้องระบุรหัสสินค้าที่รับเข้า');
    }
    if (location === '') {
      problems.push('ต้องระบุรหัสที่เก็บที่ของเข้าไปอยู่');
    }
    if (!isPositiveDecimal(quantity)) {
      problems.push('จำนวนที่รับเข้าต้องเป็นตัวเลขมากกว่า 0');
    }
    if (postingDate === null) {
      problems.push('ต้องระบุวันที่ที่มีผลทางบัญชี และต้องเป็นวันที่ที่มีอยู่จริง');
    }

    if (problems.length > 0 || postingDate === null) {
      setState({ status: 'invalid', problems });
      return;
    }

    const body: CreateGoodsReceiptBody = {
      plant_code: plant,
      product_code: product,
      storage_location_code: location,
      quantity,
      posting_date: postingDate,
      supplier_lot_id: supplierLot === '' ? null : supplierLot,
    };

    /**
     * ลายนิ้วมือของคำขอ = ไบต์ที่กำลังจะถูกส่งจริง ไม่ใช่ state ของฟอร์ม
     *
     * ใช้ตัว serialise ตัวเดียวกับที่ client ใช้ส่งจริง คีย์จึงเปลี่ยนก็ต่อเมื่อ
     * **สิ่งที่เซิร์ฟเวอร์จะเห็น** เปลี่ยน ไม่ใช่ตอนที่ผู้ใช้แค่ขยับ cursor
     */
    const next = submissionFor(submission, JSON.stringify(serializeCreateGoodsReceiptBody(body)));
    setSubmission(next);
    setState({ status: 'saving' });

    const result = await createGoodsReceipt(body, next.key, { apiBaseUrl, fetchImpl });
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status === 'ready') {
      // ใบนี้จบแล้ว คำขอครั้งถัดไปคือใบใหม่ และต้องได้คีย์ใหม่
      setSubmission(null);
      setState({ status: 'posted', receipt: result.receipt });
      return;
    }
    setState({ status: 'failed', failure: result });
  };

  if (state.status === 'posted') {
    const { receipt } = state;
    const onHold = receipt.quality_status === QUALITY_HOLD;

    return (
      <div className="cap-page" data-testid="goods-receipt-posted">
        <div className="cap-page__head">
          <div>
            <h1 className="cap-page__title">
              บันทึกการรับเข้าแล้ว
              <InfoHint
                label="เกี่ยวกับการรับเข้าที่เพิ่งบันทึก"
                text="ของเข้าสต๊อกแล้วจริง พร้อม lot ใหม่ ความเคลื่อนไหวสต๊อก และเอกสารบัญชี
              ในธุรกรรมเดียวกัน"
              />
            </h1>
          </div>
          <div className="cap-page__actions">
            <Link
              className="cap-button cap-button--ghost"
              data-testid="goods-receipt-posted-list"
              to={{ pathname, search: listHref }}
            >
              กลับไปหน้ารายการ
            </Link>
          </div>
        </div>

        {/*
          ป้ายสถานะคุณภาพมาก่อนรายละเอียดอื่นโดยตั้งใจ — คำถามแรกของพนักงานคลัง
          หลังรับของคือ "เบิกไปใช้ได้เลยไหม" และคำตอบคือยัง
        */}
        <div
          className="cap-notice"
          data-testid="goods-receipt-quality-hold"
          data-quality-status={receipt.quality_status}
          role="status"
        >
          <StatusBadge tone={qualityStatusTone(receipt.quality_status)}>
            {qualityStatusLabel(receipt.quality_status)}
          </StatusBadge>
          <h2 className="cap-notice__title">
            {onHold ? 'ของกองนี้ยังเบิกไปใช้ไม่ได้' : 'สถานะคุณภาพของ lot นี้'}
          </h2>
          <p className="cap-notice__text">
            lot ที่สร้างขึ้นคือ{' '}
            <code className="cap-table__code" data-testid="goods-receipt-lot-id">
              {receipt.lot_id}
            </code>{' '}
            และสถานะคุณภาพของมันคือ{' '}
            <code className="cap-table__code" data-testid="goods-receipt-quality-status">
              {receipt.quality_status}
            </code>
            {onHold
              ? ' — ของที่รับเข้าทุกกองเริ่มที่การกักรอตรวจเสมอ (D-059) จำนวนนี้อยู่ในสต๊อกแล้วก็จริง แต่ยังเบิกไปผลิตไม่ได้จนกว่าผู้มีสิทธิ์อนุมัติคุณภาพจะตัดสินปล่อยผ่าน และผู้ตัดสินต้องไม่ใช่คนเดียวกับผู้รับของเข้า'
              : ' — อ่านค่าจากเซิร์ฟเวอร์ตรง ๆ หน้าจอนี้ไม่ได้เป็นผู้กำหนดค่านี้'}
          </p>
          <p className="cap-notice__note">
            หน้าจอตัดสินคุณภาพยังไม่เปิดใช้งานในบิลด์นี้ — แจ้งผู้มีสิทธิ์อนุมัติคุณภาพให้ทราบ
            พร้อมรหัส lot ด้านบน
          </p>
        </div>

        <section className="cap-card" aria-label="รายละเอียดใบรับเข้าที่เพิ่งบันทึก">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              ใบรับเข้าที่เพิ่งบันทึก
              <InfoHint
                label="เกี่ยวกับค่าด้านล่าง"
                text="ค่าทั้งหมดเป็นค่าที่เซิร์ฟเวอร์ยืนยันกลับมา"
              />
            </h2>
          </div>
          <div className="cap-meta cap-meta--flush">
            <div className="cap-meta__item">
              <div className="cap-meta__label">เลขที่ใบรับเข้า</div>
              <div
                className="cap-meta__value cap-table__code"
                data-testid="goods-receipt-receipt-id"
              >
                {receipt.receipt_id}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สินค้า</div>
              <div className="cap-meta__value cap-table__code">{receipt.product_code}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">จำนวนที่รับเข้า</div>
              {/* NUMERIC เป็น string ตลอดเส้นทาง — ไม่จัดรูปแบบซ้ำที่นี่ */}
              <div className="cap-meta__value cap-table__code" data-testid="goods-receipt-quantity">
                {receipt.quantity}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">โรงงาน / ที่เก็บ</div>
              <div className="cap-meta__value cap-table__code">
                {receipt.plant_code} / {receipt.storage_location_code}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">วันที่ที่มีผล</div>
              <div className="cap-meta__value cap-table__code">{receipt.posting_date}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">เลขล็อตของผู้ขาย</div>
              <div className="cap-meta__value cap-table__code">
                {receipt.supplier_lot_id ?? '—'}
              </div>
            </div>
          </div>
          <div className="cap-form__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="goods-receipt-another"
              onClick={resetForNextReceipt}
            >
              รับของเข้าอีกใบ
            </button>
            <Link
              className="cap-button cap-button--ghost"
              data-testid="goods-receipt-posted-detail"
              to={{ pathname, search: receiptDetailSearch(receipt.receipt_id) }}
            >
              เปิดใบนี้อีกครั้ง
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="cap-page" data-testid="goods-receipt-create">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            รับของเข้าสต๊อก
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  การบันทึกครั้งนี้ทำให้ของ <strong>เข้าสต๊อกจริง</strong> พร้อมสร้าง lot ใหม่และเอกสารบัญชี
                  ในธุรกรรมเดียวกัน — ล้มจุดใดก็ไม่เหลืออะไรเลย
                </>
              }
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          <Link
            className="cap-button cap-button--ghost"
            data-testid="goods-receipt-create-cancel"
            to={{ pathname, search: listHref }}
          >
            กลับไปหน้ารายการ
          </Link>
        </div>
      </div>

      <form className="cap-card cap-card--form" onSubmit={(event) => void submit(event)}>
        <div className="cap-card__head">
          <h2 className="cap-card__title">
            ข้อมูลการรับเข้า
            <InfoHint
              label="เกี่ยวกับฟอร์มนี้"
              text="หกช่องนี้คือทั้งหมดที่ API รับ — สถานะคุณภาพ มูลค่า และผังบัญชีเป็นของเซิร์ฟเวอร์"
            />
          </h2>
        </div>

        <div className="cap-form__grid">
          <label className="cap-field">
            <span className="cap-field__label">โรงงาน (plant_code)</span>
            <select
              className="cap-input"
              name="plant_code"
              data-testid="goods-receipt-create-plant"
              value={plantCode}
              onChange={(event) => {
                setPlantCode(event.target.value);
              }}
            >
              {plants.length === 0 ? <option value="">— ไม่มีโรงงานที่คุณเห็น —</option> : null}
              {plants.map((code) => (
                <option key={code} value={code}>
                  โรงงาน {code}
                </option>
              ))}
            </select>
            <InfoHint
              label="เกี่ยวกับช่องโรงงาน"
              text="บังคับเสมอและเป็นขอบเขตสิทธิ์ ไม่ใช่ตัวกรอง (D-004) — โรงงานนอกขอบเขตเซิร์ฟเวอร์ตอบ 403"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">รหัสสินค้า (product_code)</span>
            <input
              className="cap-input"
              type="text"
              name="product_code"
              data-testid="goods-receipt-create-product"
              value={productCode}
              onChange={(event) => {
                setProductCode(event.target.value);
              }}
              placeholder="เช่น RM-STEEL-8MM"
            />
            <InfoHint
              label="เกี่ยวกับช่องรหัสสินค้า"
              text="ต้องเป็นสินค้าที่ ACTIVE และมีข้อมูลระดับโรงงานของโรงงานที่เลือก — เซิร์ฟเวอร์เป็นผู้ตรวจ"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">ที่เก็บ (storage_location_code)</span>
            <input
              className="cap-input"
              type="text"
              name="storage_location_code"
              data-testid="goods-receipt-create-location"
              value={storageLocationCode}
              onChange={(event) => {
                setStorageLocationCode(event.target.value);
              }}
              placeholder="เช่น RM-01"
            />
            <InfoHint
              label="เกี่ยวกับช่องที่เก็บ"
              text="รหัสที่เก็บซ้ำกันได้ข้ามโรงงาน จึงถูกตรวจคู่กับโรงงานที่เลือกเสมอ"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">จำนวนที่รับเข้า (quantity)</span>
            <input
              className="cap-input"
              type="text"
              inputMode="decimal"
              name="quantity"
              data-testid="goods-receipt-create-quantity"
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
              }}
              placeholder="เช่น 250.0000"
            />
            <InfoHint
              label="เกี่ยวกับช่องจำนวน"
              text="มากกว่า 0 — ส่งเป็นข้อความตามที่พิมพ์ ไม่ปัดเศษและไม่แปลงเป็นตัวเลขระหว่างทาง"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">วันที่ที่มีผล (posting_date)</span>
            <input
              className="cap-input"
              type="date"
              name="posting_date"
              data-testid="goods-receipt-create-posting-date"
              value={postingDay}
              onChange={(event) => {
                setPostingDay(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับช่องวันที่ที่มีผล"
              text="ตีความเป็นเวลาของโรงงาน (+07:00) เสมอ ไม่ใช่เขตเวลาของเครื่องคุณ (D-055) —
              งวดบัญชีของวันนี้ต้องเปิดอยู่"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">เลขล็อตของผู้ขาย (supplier_lot_id)</span>
            <input
              className="cap-input"
              type="text"
              name="supplier_lot_id"
              data-testid="goods-receipt-create-supplier-lot"
              value={supplierLotId}
              onChange={(event) => {
                setSupplierLotId(event.target.value);
              }}
              placeholder="เช่น SUP-2026-0042 (ไม่บังคับ)"
            />
            <InfoHint
              label="เกี่ยวกับช่องเลขล็อตของผู้ขาย"
              text="เว้นว่างได้ ของที่มาโดยไม่มีเลขล็อตของผู้ขายเป็นเรื่องปกติ — เลขที่กรอกเองขึ้นมาแทน
              จะแย่กว่าการปล่อยว่างตามจริง"
            />
          </label>
        </div>

        <div className="cap-notice" data-testid="goods-receipt-server-owned" role="note">
          <StatusBadge tone="warning">ของที่รับเข้าจะยังเบิกไปใช้ไม่ได้</StatusBadge>
          <h3 className="cap-notice__title">
            ไม่มีช่อง “สถานะคุณภาพ” ให้เลือกโดยตั้งใจ
            <InfoHint
              label="เกี่ยวกับการไม่มีช่องสถานะคุณภาพ"
              text={
                <>
                  <p>
                    lot ทุกใบที่การรับเข้าสร้างขึ้นเริ่มที่{' '}
                    <code className="cap-table__code">{QUALITY_HOLD}</code> เสมอ (D-059) — ของจะเข้าสต๊อก
                    ก็จริง แต่เบิกไปผลิตไม่ได้จนกว่าผู้มีสิทธิ์อนุมัติคุณภาพจะตัดสินปล่อยผ่าน
                    การกักที่ข้ามได้ด้วยการส่งค่าหนึ่งมากับคำขอไม่ใช่การกัก หน้าจอนี้จึงไม่ส่งฟิลด์นี้ไปเลย
                  </p>
                  <p>
                    มูลค่าของใบนี้คำนวณจากต้นทุนต่อหน่วยของสินค้าในโรงงานที่เลือก และผังบัญชีมาจากการตั้งค่า
                    ของฝ่ายบัญชี — ทั้งสองอย่างเป็นของเซิร์ฟเวอร์ และระบบจะปฏิเสธแทนที่จะเดา
                  </p>
                </>
              }
            />
          </h3>
        </div>

        {state.status === 'invalid' ? (
          <div className="cap-notice" data-testid="goods-receipt-create-invalid" role="alert">
            <StatusBadge tone="warning">ยังส่งไม่ได้</StatusBadge>
            <h3 className="cap-notice__title">ตรวจข้อมูลในฟอร์มก่อน</h3>
            <ul>
              {state.problems.map((problem) => (
                <li className="cap-notice__text" key={problem}>
                  {problem}
                </li>
              ))}
            </ul>
            <p className="cap-notice__note">
              ยังไม่มีคำขอถูกส่งออกไป และยังไม่มีคีย์กันซ้ำถูกใช้ —
              การตรวจที่หน้าจอเป็นเพียงการไม่ให้รอคำตอบที่รู้อยู่แล้ว เซิร์ฟเวอร์ยังตรวจทุกข้อซ้ำเองเสมอ
            </p>
          </div>
        ) : null}

        {state.status === 'failed' ? (
          <GoodsReceiptFailureNotice
            failure={state.failure}
            context="create"
            plantCode={plantCode}
          />
        ) : null}

        <div className="cap-form__actions">
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="goods-receipt-create-submit"
            disabled={state.status === 'saving'}
          >
            {state.status === 'saving' ? 'กำลังบันทึก…' : 'บันทึกการรับเข้า'}
          </button>
          {state.status === 'failed' ? (
            <span className="cap-card__hint" data-testid="goods-receipt-retry-note">
              กดบันทึกซ้ำได้เลยโดยไม่ต้องแก้อะไร — คำขอเดิมจะถูกส่งด้วยคีย์กันซ้ำตัวเดิม
              จึงไม่มีทางได้ของเข้าสองครั้ง
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
